import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CheckboxField,
  Container,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  FormField,
  IconButton,
  Input,
  KpiCard,
  NoPermissionState,
  Notice,
  OtpInput,
  PageHeader,
  PasswordInput,
  Select,
  SettingSwitch,
  Skeleton,
  StatusBadge,
  Tab,
  TabPanel,
  Table,
  TableWrap,
  Tabs,
  TabsList,
  Td,
  Textarea,
  Th,
  Toaster,
  Tooltip,
  TooltipProvider,
  Tr,
  toast,
} from '@horevia/ui';
import { createFileRoute } from '@tanstack/react-router';
import { Bell, Inbox, Pencil, Search, Trash, Users } from 'lucide-react';
import { useState } from 'react';
import { formatAmount, formatDate } from '@/lib/format';
import { useTheme } from '@/lib/theme';

/**
 * Page de démonstration du design system — **développement uniquement**.
 * Elle sert de référence visuelle rapide (états, variantes, thème sombre)
 * en l'absence de Storybook.
 */
export const Route = createFileRoute('/dev/ui')({
  component: import.meta.env.DEV ? DevUiPage : NotAvailable,
});

function NotAvailable() {
  return (
    <Container>
      <NoPermissionState title="Indisponible" description="Page réservée au développement." />
    </Container>
  );
}

const STATUSES = [
  'draft',
  'pending',
  'option',
  'confirmed',
  'checkedin',
  'checkedout',
  'cancelled',
  'noshow',
] as const;

function DevUiPage() {
  const { theme, toggle } = useTheme();
  const [otp, setOtp] = useState('');
  const [checked, setChecked] = useState(true);
  const [switched, setSwitched] = useState(true);
  const [select, setSelect] = useState('fr');

  return (
    <TooltipProvider>
      <div className="main">
        <Container>
          <PageHeader
            title="Design system HOREVIA"
            subtitle="Aperçu des composants de @horevia/ui — page de développement."
            actions={
              <Button variant="secondary" onClick={toggle}>
                Thème : {theme === 'dark' ? 'sombre' : 'clair'}
              </Button>
            }
          />

          <div className="stack stack--lg">
            <Card>
              <CardHeader title="Boutons" />
              <CardBody>
                <div className="row row--wrap">
                  <Button variant="primary">Primaire</Button>
                  <Button variant="secondary">Secondaire</Button>
                  <Button variant="ghost">Discret</Button>
                  <Button variant="soft">Doux</Button>
                  <Button variant="danger">
                    <Trash className="ico" aria-hidden="true" /> Danger
                  </Button>
                  <Button variant="primary" loading>
                    Chargement
                  </Button>
                  <Button variant="primary" disabled>
                    Désactivé
                  </Button>
                  <Button variant="primary" size="sm">
                    Petit
                  </Button>
                  <Button variant="primary" size="lg">
                    Grand
                  </Button>
                  <Tooltip content="Bouton icône">
                    <IconButton label="Modifier">
                      <Pencil className="ico ico-16" aria-hidden="true" />
                    </IconButton>
                  </Tooltip>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Formulaires" />
              <CardBody>
                <div className="stack">
                  <div className="field-row">
                    <FormField label="Adresse e-mail" hint="Adresse professionnelle" required>
                      {(field) => (
                        <Input
                          id={field.id}
                          icon={<Search className="ico" aria-hidden="true" />}
                          placeholder="prenom.nom@exemple.com"
                        />
                      )}
                    </FormField>
                    <FormField label="Mot de passe" error="Au moins 12 caractères" required>
                      {(field) => (
                        <PasswordInput
                          id={field.id}
                          invalid
                          showLabel="Afficher"
                          hideLabel="Masquer"
                        />
                      )}
                    </FormField>
                  </div>
                  <div className="field-row">
                    <FormField label="Montant" hint="Aucune décimale en XOF">
                      {(field) => <Input id={field.id} suffix="XOF" defaultValue="35 000" />}
                    </FormField>
                    <FormField label="Langue">
                      {(field) => (
                        <Select
                          id={field.id}
                          value={select}
                          onValueChange={setSelect}
                          options={[
                            { value: 'fr', label: 'Français' },
                            { value: 'en', label: 'Anglais' },
                          ]}
                        />
                      )}
                    </FormField>
                  </div>
                  <FormField label="Motif">{(field) => <Textarea id={field.id} />}</FormField>
                  <CheckboxField
                    label="Se souvenir de cet appareil"
                    description="30 jours"
                    checked={checked}
                    onCheckedChange={(value) => setChecked(value === true)}
                  />
                  <SettingSwitch
                    label="Double authentification"
                    description="Code à 6 chiffres à chaque connexion"
                    checked={switched}
                    onCheckedChange={setSwitched}
                  />
                  <div className="field">
                    <span className="label">Code de vérification</span>
                    <OtpInput value={otp} onChange={setOtp} label="Code de vérification" />
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Badges et statuts" />
              <CardBody>
                <div className="row row--wrap mb-4">
                  {(
                    ['neutral', 'violet', 'blue', 'green', 'amber', 'red', 'cyan', 'dark'] as const
                  ).map((tone) => (
                    <Badge key={tone} tone={tone} dot>
                      {tone}
                    </Badge>
                  ))}
                </div>
                <div className="row row--wrap">
                  {STATUSES.map((status) => (
                    <StatusBadge key={status} status={status}>
                      {status}
                    </StatusBadge>
                  ))}
                </div>
              </CardBody>
            </Card>

            <div className="grid grid-4">
              <KpiCard
                tone="violet"
                icon={<Users className="ico ico-16" />}
                label="Utilisateurs"
                value={6}
                footer="1 invitation"
              />
              <KpiCard
                tone="green"
                icon={<Bell className="ico ico-16" />}
                label="MFA"
                value={4}
                unit=" / 6"
                footer="2 à sécuriser"
              />
              <KpiCard
                tone="blue"
                label="Encaissé"
                value={formatAmount(1275000)}
                footer={formatDate(new Date().toISOString())}
              />
              <KpiCard tone="amber" label="Chargement" value={0} loading />
            </div>

            <Card>
              <CardHeader title="Tableau" />
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>Utilisateur</Th>
                      <Th>Rôle</Th>
                      <Th align="right">Montant</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Bertrand PARE', 'Gérante (propriétaire)', 450000],
                      ['Awa Sawadogo', 'Manager', 225000],
                      ['Issouf Kaboré', 'Réception', 60000],
                    ].map(([name, role, amount]) => (
                      <Tr key={name as string}>
                        <Td>
                          <div className="cell-user">
                            <Avatar name={name as string} size="sm" />
                            <span>
                              <span className="cell-user__name">{name}</span>
                              <span className="cell-user__sub">stayafrica.bf</span>
                            </span>
                          </div>
                        </Td>
                        <Td>
                          <Badge tone="violet">{role}</Badge>
                        </Td>
                        <Td align="right" numeric>
                          {formatAmount(amount as number)}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </Card>

            <Card>
              <CardHeader title="Onglets, menus, superpositions" />
              <CardBody>
                <Tabs defaultValue="one">
                  <TabsList>
                    <Tab value="one" count={6}>
                      Utilisateurs
                    </Tab>
                    <Tab value="two" count={7}>
                      Rôles
                    </Tab>
                  </TabsList>
                  <TabPanel value="one">
                    <div className="row row--wrap">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="secondary">Ouvrir une modale</Button>
                        </DialogTrigger>
                        <DialogContent
                          title="Désactiver Fatimata Zongo ?"
                          description="L'utilisatrice perdra immédiatement l'accès."
                          closeLabel="Fermer"
                          footer={
                            <>
                              <DialogClose asChild>
                                <Button variant="ghost">Annuler</Button>
                              </DialogClose>
                              <DialogClose asChild>
                                <Button variant="danger">Désactiver le compte</Button>
                              </DialogClose>
                            </>
                          }
                        />
                      </Dialog>

                      <Drawer>
                        <DrawerTrigger asChild>
                          <Button variant="secondary">Ouvrir un tiroir</Button>
                        </DrawerTrigger>
                        <DrawerContent
                          title="Inviter un utilisateur"
                          description="Un courriel d'activation sera envoyé."
                          closeLabel="Fermer"
                          footer={
                            <>
                              <DrawerClose asChild>
                                <Button variant="ghost">Annuler</Button>
                              </DrawerClose>
                              <Button variant="primary">Envoyer l'invitation</Button>
                            </>
                          }
                        >
                          <FormField label="Adresse e-mail" required>
                            {(field) => <Input id={field.id} type="email" />}
                          </FormField>
                        </DrawerContent>
                      </Drawer>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="secondary">Menu</Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem>Consulter</DropdownMenuItem>
                          <DropdownMenuItem>Modifier</DropdownMenuItem>
                          <DropdownMenuItem danger>Désactiver</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <Button
                        variant="secondary"
                        onClick={() =>
                          toast.success('Invitation envoyée à m.ouedraogo@stayafrica.bf.')
                        }
                      >
                        Toast
                      </Button>
                    </div>
                  </TabPanel>
                  <TabPanel value="two">
                    <p className="muted">Second onglet.</p>
                  </TabPanel>
                </Tabs>
              </CardBody>
            </Card>

            <div className="grid grid-2">
              <Card>
                <CardHeader title="Bandeaux" />
                <CardBody>
                  <div className="stack stack--md">
                    <Notice tone="info" title="Information">
                      Le sélecteur d'établissement apparaît dès le deuxième site.
                    </Notice>
                    <Notice tone="warning" title="Attention">
                      Deux comptes n'ont pas activé la double authentification.
                    </Notice>
                    <Notice tone="danger" title="Erreur">
                      La facture ne peut pas être émise : documents manquants.
                    </Notice>
                    <Notice tone="success" title="Enregistré">
                      Paiement de {formatAmount(50000)} enregistré.
                    </Notice>
                    <Notice tone="violet" title="Sécurité">
                      Sessions expirées après 12 heures.
                    </Notice>
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardHeader title="États" />
                <CardBody>
                  <div className="stack stack--md">
                    <Skeleton variant="title" />
                    <Skeleton variant="line" width="80%" />
                    <Skeleton variant="pill" />
                    <EmptyState
                      icon={<Inbox className="ico ico-24" aria-hidden="true" />}
                      title="Aucun utilisateur"
                      description="Invitez un collaborateur pour lui donner accès."
                      action={<Button variant="primary">Inviter</Button>}
                    />
                    <ErrorState
                      title="Une erreur est survenue"
                      description="Le serveur est injoignable."
                      traceId="7f2a9c41b8e04d6f"
                      retryLabel="Réessayer"
                      onRetry={() => undefined}
                    />
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </Container>
        <Toaster />
      </div>
    </TooltipProvider>
  );
}
